import React from 'react';
import PropTypes from 'prop-types';
import Typography from "@material-ui/core/Typography";
import IconButton from "@material-ui/core/IconButton";
import Icon from "@material-ui/core/Icon";
import {TextField, Tooltip} from "@material-ui/core";
import FormatBoldIcon from '@material-ui/icons/FormatBold';
import FormatItalicIcon from '@material-ui/icons/FormatItalic';
import FormatUnderlinedIcon from '@material-ui/icons/FormatUnderlined';
import FormatColorFillIcon from '@material-ui/icons/FormatColorFill';
import FormatColorText from '@material-ui/icons/FormatColorText';
import ToggleButton from '@material-ui/lab/ToggleButton';
import ToggleButtonGroup from '@material-ui/lab/ToggleButtonGroup';
import Autocomplete from "@material-ui/lab/Autocomplete";
import withStyles from "@material-ui/styles/withStyles";
import {Draggable} from "react-beautiful-dnd";
import ColorPickerPopover from "../../../../../../../Utils/ColorPickerPopover";

const styles = () => ({
    conditionItem: {
        display: 'flex',
        height: 30,
        cursor: 'pointer',
        justifyContent: 'space-between',
        '&:hover': {
            background: "rgba(0,0,0,0.01)"
        }
    },
    inputRoot: {
        paddingRight: "23px !important",
        '&[class*="MuiInput-root"]': {
            padding: 1,
            '& input:first-child': {
                padding: '5px 0 !important',
            },
        },
    },
    paper: {
        width: 150,
        '& > ul': {
            maxHeight: 200,
            overflow: 'auto',
        },
    },

    option: {
        minHeight: 2,
        display: 'flex',
        justifyContent: 'flex-start',
        alignItems: 'center',
        cursor: 'pointer',
        paddingTop: 1,
        boxSizing: 'border-box',
        outline: '0',
        WebkitTapHighlightColor: 'transparent',
        paddingBottom: 2,
        paddingLeft: 16,
        paddingRight: 16,
    },
});

const ConditionalFormatSetup = (props) => {
    const {
        classes,
        conditions = [],
        removeCondition,
        currentOperands,
        onChangeValue,
        onChangeCriteria,
        onChangeFormats,
        onChangeColor
    } = props;
    const [conditionOptions] = React.useState(() => currentOperands);
    const [hoveredItem, setHoveredItem] = React.useState(() => null);
    const [colorPicker, setColorPicker] = React.useState(null);
    return (
        <>
            {conditions.length > 0 ? (
                conditions.map((item, index) => {
                    return (
                        <Draggable
                            key={`condition_${index}`}
                            draggableId={`condition_${index}`}
                            index={index}
                        >
                            {(provided) => (
                                <div
                                    ref={provided.innerRef}
                                    {...provided.draggableProps}
                                >
                                    <Condition
                                        classes={classes}
                                        item={item}
                                        index={index}
                                        hoveredItem={hoveredItem}
                                        setHoveredItem={setHoveredItem}
                                        conditionOptions={conditionOptions}
                                        removeCondition={removeCondition}
                                        onChangeValue={onChangeValue}
                                        onChangeCriteria={onChangeCriteria}
                                        onChangeFormats={onChangeFormats}
                                        colorPicker={colorPicker}
                                        setColorPicker={setColorPicker}
                                        onChangeColor={onChangeColor}
                                        dragHandleProps={provided.dragHandleProps}
                                    />
                                </div>
                            )}
                        </Draggable>
                    )
                })
            ) : (
                <NoConditionsMessage/>
            )}

        </>
    )
};

const Condition = (props) => {
    return (
        <div style={{width: '100%'}}>
            <div
                onMouseEnter={() => props.setHoveredItem(props.index)}
                onMouseLeave={() => props.setHoveredItem(null)}
                style={{display: "flex", borderBottom: '1px solid rgba(0,0,0,0.07)', height: 30}}>
                <DragIcon
                    hoveredItem={props.hoveredItem}
                    index={props.index}
                    item={props.item}
                    dragHandleProps={props.dragHandleProps}
                />
                <SelectCondition
                    item={props.item}
                    index={props.index}
                    classes={props.classes}
                    options={props.conditionOptions}
                    onChangeCriteria={props.onChangeCriteria}
                    onChangeValue={props.onChangeValue}
                />
                <TextFormat
                    item={props.item}
                    index={props.index}
                    setFormats={props.onChangeFormats}
                    colorPicker={props.colorPicker}
                    setColorPicker={props.setColorPicker}
                    onChangeColor={props.onChangeColor}
                />
                <PreviewFormat
                    item={props.item}
                />
                <RemoveConditionIcon
                    removeCondition={props.removeCondition}
                    index={props.index}
                    hoveredItem={props.hoveredItem}
                />
            </div>
        </div>
    )
};


//format components
const DragIcon = (props) => {
    return (
        <div {...props.dragHandleProps} style={{display: 'flex', alignItems: 'center'}}>
            <Icon fontSize={"small"} style={{opacity: props.hoveredItem === props.index ? '1' : '0'}}>
                drag_indicator
            </Icon>
        </div>
    )
};
const SelectCondition = (props) => {
    let criteriaValue =
        props.item.criteria ?
            {id: props.item.criteria, name: getOperandName(props.item.criteria, props.options)} :
            {id: "", name: ""};
    let value = props.item.value ? props.item.value : "";
    return (
        <>
            <Autocomplete
                value={criteriaValue}
                options={props.options}
                getOptionLabel={(o) => o.name}
                disableClearable={true}
                classes={{
                    inputRoot: props.classes.inputRoot,
                    option: props.classes.option,
                    paper: props.classes.paper
                }}
                onChange={(event, value) => props.onChangeCriteria(value, props.index)}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        style={{width: 130}}
                        placeholder={'Select criteria'}
                        InputProps={{
                            ...params.InputProps,
                            disableUnderline: true,
                        }}
                    />
                )}
            />
            <TextField
                placeholder={'Enter value'}
                value={value}
                onChange={(event) => props.onChangeValue(event, props.index)}
                InputProps={{
                    style: {width: '110px'},
                    disableUnderline: true,
                }}
            />
        </>
    )
};
const TextFormat = (props) => {
    let values = [];
    props.item.formats && Object.keys(props.item.formats).forEach(i => props.item.formats[i] === true && values.push(i));
    return (
        <>
            <ToggleButtonGroup value={values} onChange={(event, formats) => props.setFormats(formats, props.index)}
                               aria-label="text formatting">
                <ToggleButton value="bold" aria-label="bold" style={{height: 30, borderRadius: 0, padding: 0}}>
                    <FormatBoldIcon fontSize={"small"}/>
                </ToggleButton>
                <ToggleButton fontSize={"small"} value="italic" aria-label="italic" style={{height: 30, padding: 0}}>
                    <FormatItalicIcon/>
                </ToggleButton>
                <ToggleButton fontSize={"small"} value="underlined" aria-label="underlined"
                              style={{height: 30, padding: 0, borderRadius: 0}}>
                    <FormatUnderlinedIcon/>
                </ToggleButton>
            </ToggleButtonGroup>
            <BackgroundPicker
                colorPicker={props.colorPicker}
                setColorPicker={props.setColorPicker}
                onChangeColor={props.onChangeColor}
                index={props.index}
                item={props.item}
            />
            <ColorPicker
                colorPicker={props.colorPicker}
                setColorPicker={props.setColorPicker}
                onChangeColor={props.onChangeColor}
                index={props.index}
                item={props.item}
            />
        </>
    )
};
const BackgroundPicker = (props) => {
    const formats = props.item.formats ? props.item.formats : {};
    const color = formats.background ? formats.background : '';
    return (
        <>
            <IconButton fontSize={"small"}
                        style={{
                            height: 33,
                            borderRadius: 0,
                            padding: 0,
                            color: formats.background ? color : undefined
                        }}
                        onClick={(e) => props.colorPicker ? props.setColorPicker(null) : props.setColorPicker({
                            name: 'background',
                            target: e.currentTarget,
                            index: props.index
                        })}
            >
                <FormatColorFillIcon/>
            </IconButton>
            <ColorPickerPopover
                open={Boolean(props.colorPicker && props.colorPicker.name === 'background')}
                close={() => props.setColorPicker(null)}
                anchorEl={props.colorPicker && props.colorPicker.target}
                complete={props.onChangeColor}
                name={'background'}
                type={"sketch"}
                color={color}
                path={props.colorPicker && props.colorPicker.index}
            />
        </>
    )
};
const ColorPicker = (props) => {
    const formats = props.item.formats ? props.item.formats : {};
    const color = formats.color ? formats.color : {};
    return (
        <>
            <IconButton fontSize={"small"}
                        style={{
                            height: 33,
                            borderRadius: 0,
                            padding: 0,
                            color: formats.color ? color : undefined
                        }}
                        onClick={(e) => props.setColorPicker({
                            name: 'color',
                            target: e.currentTarget,
                            index: props.index
                        })}
            >
                <FormatColorText/>
            </IconButton>
            <ColorPickerPopover
                open={Boolean(props.colorPicker && props.colorPicker.name === 'color')}
                close={() => props.setColorPicker(null)}
                anchorEl={props.colorPicker && props.colorPicker.target}
                complete={props.onChangeColor}
                name={'color'}
                type={"sketch"}
                color={color}
                path={props.colorPicker && props.colorPicker.index}
            />
        </>
    )
};
const PreviewFormat = (props) => {
    const formats = props.item.formats ? props.item.formats : {};
    return (
        <Tooltip title={'Preview format'}>
            <div style={{
                width: 44,
                height: 30,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                borderRadius: 0,
                background: formats.background
            }}>
            <span
                style={{
                    fontStyle: formats.italic && 'italic',
                    fontWeight: formats.bold && 'bold',
                    textDecoration: formats.underlined && 'underline',
                    color: formats.color ? formats.color : '#000'
                }}
            >123</span>
            </div>
        </Tooltip>
    )
};
const RemoveConditionIcon = (props) => {
    return (
        <IconButton style={{width: 30, opacity: props.hoveredItem === props.index ? '1' : '0'}}
                    onClick={() => {
                        props.removeCondition(props.index)
                    }}>
            <Icon fontSize={"small"}>
                delete
            </Icon>
        </IconButton>
    )
};

const NoConditionsMessage = () => {
    return (
        <div style={{textAlign: 'center', paddingTop: 5}}>
            <Typography>No format conditions</Typography>
        </div>
    )
};

function getOperandName(value, operands) {
    let name = "";
    operands.forEach(o => {
        if (o.id === value) {
            name = o.name;
        }
    });

    return name;
}


ConditionalFormatSetup.propTypes = {
    conditions: PropTypes.array,
    currentOperands: PropTypes.array,
    createCondition: PropTypes.func.isRequired,
    removeCondition: PropTypes.func.isRequired,
    onChangeValue: PropTypes.func.isRequired,
    onChangeCriteria: PropTypes.func.isRequired,
    onChangeFormats: PropTypes.func.isRequired,
    onChangeColor: PropTypes.func.isRequired
};
export default withStyles(styles)(ConditionalFormatSetup);